import { memo } from "react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { PopoverProps } from "@productlane/ui"
import { PainLevel } from "@productlane/db/browser"
import { cn } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	Button,
	Popover,
	PriorityIcons,
	PriorityNaming,
	Shortcut,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import { RoleCheck } from "@/components/shared/role-check"
import { useShortcut } from "@/lib"
import { useRole } from "@/lib/useRole"

interface PainLevelSelectProps {
	painLevel: PainLevel
	onChange: (painLevel: PainLevel) => void
	className?: string
	align?: PopoverProps["align"]
	modal?: PopoverProps["modal"]
}

// const levels = [PainLevel.UNKNOWN, PainLevel.HIGH]
const levels = [
	PainLevel.UNKNOWN,
	PainLevel.HIGH,
	PainLevel.MEDIUM,
	PainLevel.LOW,
]

export const PainLevelSelect = memo(
	({ onChange, painLevel, className, align, modal }: PainLevelSelectProps) => {
		const { role } = useRole()
		const [open, onOpenChange] = useToggle(false)

		const SelectedIcon = PriorityIcons[painLevel]

		useShortcut(
			"p",
			() => {
				onOpenChange(true)
			},
			{
				enableOnContentEditable: false,
				preventDefault: true,
				enabled: role !== "VIEWER",
			},
		)

		return (
			<Popover
				open={open}
				onOpenChange={onOpenChange}
				modal={modal}
				className="p-0"
				align={align}
				triggerTooltip={
					<div className="flex gap-1">
						Change priority
						<Shortcut>{ShortcutSymbols.P}</Shortcut>
					</div>
				}
				content={
					<RoleCheck not={["VIEWER"]}>
						<Command>
							<CommandInput autoFocus placeholder="Set priority..." />
							<CommandEmpty>No matching priority</CommandEmpty>
							<CommandList>
								<CommandGroup>
									{levels.map((level) => {
										const Icon = PriorityIcons[level]
										return (
											<CommandItem
												key={level}
												onSelect={() => {
													onChange(level)
													onOpenChange(false)
												}}
											>
												<div className="flex items-center gap-2">
													<Icon className="h-4 w-4" />
													<span className="flex-1 truncate">
														{PriorityNaming[level]}
													</span>
													{painLevel === level && <Check className="h-4 w-4" />}
												</div>
											</CommandItem>
										)
									})}
								</CommandGroup>
							</CommandList>
						</Command>
					</RoleCheck>
				}
			>
				<Button
					variant="ghost"
					Icon={() => <SelectedIcon />}
					className={cn(
						"h-auto min-w-[145px] justify-start text-xs",
						painLevel === PainLevel.UNKNOWN
							? "!text-tertiary"
							: "!text-primary",
						role === "VIEWER" && "pointer-events-none",
						className,
					)}
				>
					{painLevel === PainLevel.UNKNOWN
						? "Importance"
						: PriorityNaming[painLevel]}
				</Button>
			</Popover>
		)
	},
)
